import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import { MapPin, Navigation, CheckCircle, ArrowLeft, Loader, Search, X, ChevronRight, Edit3, Map } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { authAPI } from '../../api';

import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../utils/googleMapsConfig';

const defaultCenter = { lat: 13.0827, lng: 80.2707 };

const mapStyles = [
    { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#f5f7fa' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9e8f5' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#f0f0f0' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#e8e8e8' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#d5e8d4' }] },
    { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#eef2ef' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#c9c9c9' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#777' }] },
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

const parseAddressComponents = (components) => {
    const result = {
        houseNumber: '',
        areaOrStreet: '',
        villageOrLocality: '',
        landmark: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        rawComponents: components
    };

    if (!components || !Array.isArray(components)) return result;

    let streetNumber = '';
    let route = '';
    let sublocality = '';
    let neighborhood = '';

    components.forEach((component) => {
        const types = component.types;
        if (types.includes('street_number')) {
            streetNumber = component.long_name;
        } else if (types.includes('route')) {
            route = component.long_name;
        } else if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
            sublocality = component.long_name;
        } else if (types.includes('neighborhood')) {
            neighborhood = component.long_name;
        } else if (types.includes('locality')) {
            result.city = component.long_name;
        } else if (types.includes('administrative_area_level_2')) {
            if (!result.city) {
                result.city = component.long_name;
            }
        } else if (types.includes('administrative_area_level_1')) {
            result.state = component.long_name;
        } else if (types.includes('country')) {
            result.country = component.long_name;
        } else if (types.includes('postal_code')) {
            result.postalCode = component.long_name;
        }
    });

    result.houseNumber = streetNumber;
    result.areaOrStreet = [route, neighborhood].filter(Boolean).join(', ');
    result.villageOrLocality = sublocality;
    result.landmark = ''; // User can enter landmark manually

    return result;
};

const makeRawData = (lat, lng, addr, parsed) => {
    if (!parsed.rawComponents) return "[]";
    return JSON.stringify(parsed.rawComponents.map(c => ({ name: c.long_name, types: c.types })));
};

export default function LocationPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const registrationData = location.state?.registrationData;

    useEffect(() => {
        if (!registrationData) {
            toast.error('Session expired. Please fill in your details first.');
            navigate('/register');
        }
    }, [registrationData, navigate]);

    // State
    const [markerPos, setMarkerPos] = useState(defaultCenter);
    const [address, setAddress] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [loading, setLoading] = useState(false);
    const [locating, setLocating] = useState(false);
    const [mapInstance, setMapInstance] = useState(null);
    const [activeTab, setActiveTab] = useState('search'); // 'search' | 'map'
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);

    useEffect(() => {
        if (isMapModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isMapModalOpen]);

    // Cleanly split address components
    const [parsedLocation, setParsedLocation] = useState({
        houseNumber: '',
        areaOrStreet: '',
        villageOrLocality: '',
        landmark: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        rawComponents: null
    });

    // Visual Address Textbox States
    const [tempAddressText, setTempAddressText] = useState('');
    const [permAddressText, setPermAddressText] = useState('');
    const [sameAsTemporary, setSameAsTemporary] = useState(false);

    const searchInputRef = useRef(null);
    const geocoderRef = useRef(null);
    const autocompleteServiceRef = useRef(null);
    const placesServiceRef = useRef(null);
    const debounceRef = useRef(null);
    const mapIdleDebounceRef = useRef(null);
    const lastGeocodedPosRef = useRef(null);
    const mapInstanceRef = useRef(null);

    // Modal search state
    const [modalSearchValue, setModalSearchValue] = useState('');
    const [modalSearchResults, setModalSearchResults] = useState([]);
    const [modalSearching, setModalSearching] = useState(false);
    const modalDebounceRef = useRef(null);

    const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

    // Initialize services once Google Maps JS API is loaded (independent of map rendering)
    useEffect(() => {
        if (isLoaded && window.google) {
            geocoderRef.current = new window.google.maps.Geocoder();
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();

            // Initialize PlacesService with dummy div so it works even if Map is not loaded yet
            const dummyDiv = document.createElement('div');
            placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
        }
    }, [isLoaded]);

    // Upgrade PlacesService with actual map once map renders
    const onMapLoad = useCallback((map) => {
        setMapInstance(map);
        mapInstanceRef.current = map;
        if (window.google) {
            placesServiceRef.current = new window.google.maps.places.PlacesService(map);
        }
    }, []);




    // Places API Fallback (Reverse Geocoding without Geocoding API)
    const fallbackWithPlacesAPI = useCallback(async (lat, lng) => {
        return new Promise((resolve) => {
            if (!window.google) {
                resolve(false);
                return;
            }
            if (!placesServiceRef.current) {
                const dummyDiv = document.createElement('div');
                placesServiceRef.current = new window.google.maps.places.PlacesService(mapInstanceRef.current || dummyDiv);
            }
            const location = new window.google.maps.LatLng(lat, lng);

            // Search for the nearest place within a small radius
            placesServiceRef.current.nearbySearch({
                location,
                radius: 50
            }, (results, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
                    placesServiceRef.current.getDetails({
                        placeId: results[0].place_id,
                        fields: ['address_components', 'formatted_address']
                    }, (place, detailStatus) => {
                        if (detailStatus === window.google.maps.places.PlacesServiceStatus.OK && place && place.address_components) {
                            const addr = place.formatted_address;
                            const parsed = parseAddressComponents(place.address_components);

                            setAddress(addr);
                            setSearchValue(addr);
                            setParsedLocation(parsed);
                            setTempAddressText(addr);
                            setSearchResults([]);
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    });
                } else {
                    resolve(false);
                }
            });
        });
    }, [sameAsTemporary]);

    // Reverse geocode
    const reverseGeocode = useCallback((lat, lng) => {
        if (!window.google) {
            console.error("Google Maps API not loaded");
            return;
        }
        try {
            if (!geocoderRef.current) {
                geocoderRef.current = new window.google.maps.Geocoder();
            }
            const latLng = new window.google.maps.LatLng(lat, lng);
            geocoderRef.current.geocode({ location: latLng }, async (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const addr = results[0].formatted_address;
                    const parsed = parseAddressComponents(results[0].address_components);

                    setAddress(addr);
                    setSearchValue(addr);
                    setParsedLocation(parsed);
                    setTempAddressText(addr);
                    setSearchResults([]);
                } else {
                    console.error("Geocoding failed due to: " + status);
                    const placesSuccess = await fallbackWithPlacesAPI(lat, lng);
                    if (!placesSuccess) {
                        toast.error(`Google Maps API Failed: ${status}. Cannot determine address for this location.`);
                    }
                }
            });
        } catch (error) {
            console.error("Error in reverseGeocode:", error);
            toast.error("Error finding address for this location");
        }
    }, [sameAsTemporary, fallbackWithPlacesAPI]);

    // Search predictions using Autocomplete Service
    const handleSearchInput = useCallback((e) => {
        const query = e.target.value;
        setSearchValue(query);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        // Check if query is a latitude/longitude pair
        const parts = query.split(',');
        if (parts.length === 2) {
            const lat = parseFloat(parts[0].trim());
            const lng = parseFloat(parts[1].trim());
            if (!isNaN(lat) && lat >= -90 && lat <= 90 && !isNaN(lng) && lng >= -180 && lng <= 180) {
                // Instantly show a special coordinate search result
                setSearchResults([
                    {
                        place_id: 'coordinate-result',
                        description: `📍 Go to exact coordinates: ${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                        isCoordinate: true,
                        lat: lat,
                        lng: lng
                    }
                ]);
                return;
            }
        }

        if (!query.trim() || query.length < 2) {
            setSearchResults([]);
            return;
        }

        debounceRef.current = setTimeout(() => {
            if (!window.google) return;
            if (!autocompleteServiceRef.current) {
                autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
            }
            setSearching(true);
            autocompleteServiceRef.current.getPlacePredictions(
                { input: query, componentRestrictions: { country: 'in' } },
                (predictions, status) => {
                    setSearching(false);
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                        setSearchResults(predictions);
                    } else {
                        setSearchResults([]);
                    }
                }
            );
        }, 300);
    }, []);

    // Select a prediction from the list
    const selectPrediction = useCallback((prediction) => {
        if (prediction.isCoordinate) {
            const lat = prediction.lat;
            const lng = prediction.lng;
            setMarkerPos({ lat, lng });
            reverseGeocode(lat, lng);
            if (mapInstance) {
                mapInstance.panTo({ lat, lng });
                mapInstance.setZoom(17);
            }
            setActiveTab('map');
            setSearchResults([]);
            return;
        }

        if (!window.google) return;
        if (!placesServiceRef.current) {
            const dummyDiv = document.createElement('div');
            placesServiceRef.current = new window.google.maps.places.PlacesService(mapInstance || dummyDiv);
        }
        placesServiceRef.current.getDetails(
            { placeId: prediction.place_id, fields: ['geometry', 'formatted_address', 'name', 'address_components'] },
            (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry) {
                    console.group('%c🔍 Google Places Detail Raw Response', 'color: #6366f1; font-weight: bold; font-size: 13px;');
                    console.groupEnd();

                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    const addr = place.formatted_address || place.name;

                    const parsed = parseAddressComponents(place.address_components);

                    console.group('%c🛠️ Parsed Address Fields', 'color: #4f46e5; font-weight: bold; font-size: 13px;');
                    console.table(parsed);
                    console.groupEnd();

                    setMarkerPos({ lat, lng });
                    setAddress(addr);
                    setSearchValue(addr);
                    setParsedLocation(parsed);
                    setTempAddressText(addr);
                    setSearchResults([]);

                    if (mapInstance) {
                        mapInstance.panTo({ lat, lng });
                        mapInstance.setZoom(17);
                    }
                    // Switch to map tab to show the pin
                    // setActiveTab('map');
                }
            }
        );
    }, [mapInstance, sameAsTemporary, reverseGeocode, setActiveTab]);

    // Map click handler
    const onMapClick = useCallback((e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setMarkerPos({ lat, lng });
        reverseGeocode(lat, lng);
    }, [reverseGeocode]);

    // Marker drag handler
    const onMarkerDragEnd = useCallback((e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setMarkerPos({ lat, lng });
        reverseGeocode(lat, lng);
    }, [reverseGeocode]);

    // Modal map search handler
    const handleModalSearchInput = useCallback((e) => {
        const query = e.target.value;
        setModalSearchValue(query);

        if (modalDebounceRef.current) clearTimeout(modalDebounceRef.current);

        if (!query.trim() || query.length < 2) {
            setModalSearchResults([]);
            return;
        }

        modalDebounceRef.current = setTimeout(() => {
            if (!window.google) return;
            if (!autocompleteServiceRef.current) {
                autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
            }
            setModalSearching(true);
            autocompleteServiceRef.current.getPlacePredictions(
                { input: query, componentRestrictions: { country: 'in' } },
                (predictions, status) => {
                    setModalSearching(false);
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                        setModalSearchResults(predictions);
                    } else {
                        setModalSearchResults([]);
                    }
                }
            );
        }, 300);
    }, []);

    // Select a place from modal search results — pan map to that location
    const selectModalPrediction = useCallback((prediction) => {
        if (!window.google) return;
        if (!placesServiceRef.current) {
            const dummyDiv = document.createElement('div');
            placesServiceRef.current = new window.google.maps.places.PlacesService(mapInstanceRef.current || dummyDiv);
        }
        placesServiceRef.current.getDetails(
            { placeId: prediction.place_id, fields: ['geometry', 'formatted_address', 'name', 'address_components'] },
            (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry) {
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();

                    // Pan map to this location
                    if (mapInstanceRef.current) {
                        mapInstanceRef.current.panTo({ lat, lng });
                        mapInstanceRef.current.setZoom(17);
                    }

                    // Clear modal search
                    setModalSearchValue('');
                    setModalSearchResults([]);

                    // Update marker and geocode (onIdle will also fire)
                    lastGeocodedPosRef.current = null; // force re-geocode
                    setMarkerPos({ lat, lng });
                    reverseGeocode(lat, lng);
                }
            }
        );
    }, [reverseGeocode]);

    // GPS location
    const getCurrentLocation = useCallback(async () => {
        if (!navigator.geolocation) {
            toast.error('Geolocation is not supported or disabled. It requires localhost or HTTPS to work.');
            return;
        }

        const fetchLocation = () => {
            setLocating(true);
            toast.loading('Requesting high-accuracy location from browser...', { id: 'geo-toast' });

            const successCallback = (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;

                // Force the map to pan and the pin to move to exact coordinates
                lastGeocodedPosRef.current = null;
                setMarkerPos({ lat, lng });

                if (mapInstanceRef.current) {
                    mapInstanceRef.current.panTo({ lat, lng });
                    mapInstanceRef.current.setZoom(18);
                }

                reverseGeocode(lat, lng);
                setActiveTab('map');
                setLocating(false);
                toast.success('Exact location detected!', { id: 'geo-toast' });
            };

            const errorCallback = (err) => {
                console.warn(`Geolocation error (${err.code}): ${err.message}`);

                if (err.code === 1) {
                    setLocating(false);
                    toast.dismiss('geo-toast');
                    Swal.fire({
                        title: 'Location Permission Denied',
                        text: 'Please click the padlock 🔒 icon in your browser address bar, change Location to "Allow", and try again.',
                        icon: 'error',
                        confirmButtonText: 'Got it',
                        confirmButtonColor: '#14b8a6'
                    });
                } else if (err.code === 3 || err.code === 2) {
                    // Timeout or Position Unavailable on high accuracy. Try again with low accuracy.
                    toast.loading('High accuracy failed. Trying low accuracy...', { id: 'geo-toast' });
                    navigator.geolocation.getCurrentPosition(
                        successCallback,
                        (lowErr) => {
                            setLocating(false);
                            toast.error('GPS failed. Please drag the pin on the map instead.', { id: 'geo-toast' });
                        },
                        { enableHighAccuracy: false, timeout: 15000, maximumAge: Infinity }
                    );
                } else {
                    setLocating(false);
                    toast.error('Unable to retrieve your exact location.', { id: 'geo-toast' });
                }
            };

            // First attempt: High Accuracy
            navigator.geolocation.getCurrentPosition(
                successCallback,
                errorCallback,
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            );
        };

        // Try to check permissions first for better UX
        if (navigator.permissions && navigator.permissions.query) {
            try {
                const result = await navigator.permissions.query({ name: 'geolocation' });
                if (result.state === 'denied') {
                    Swal.fire({
                        title: 'Location Blocked',
                        text: 'You have previously blocked location access for this site. To use this feature, please click the padlock 🔒 in the Chrome address bar and set Location to "Allow".',
                        icon: 'warning',
                        confirmButtonText: 'I understand',
                        confirmButtonColor: '#14b8a6'
                    });
                    return;
                } else if (result.state === 'prompt') {
                    // Pre-prompt
                    const swalResult = await Swal.fire({
                        title: 'Allow Location',
                        text: 'Please click "Allow" on the browser prompt that appears next so we can automatically find your shop location.',
                        icon: 'info',
                        showCancelButton: true,
                        confirmButtonText: 'Continue',
                        cancelButtonText: 'Cancel',
                        confirmButtonColor: '#14b8a6'
                    });
                    if (swalResult.isConfirmed) {
                        fetchLocation();
                    }
                    return;
                }
            } catch (e) {
                console.warn('Permissions API not supported', e);
            }
        }

        // Fallback or if granted
        fetchLocation();

    }, [reverseGeocode, setActiveTab]);

    // Update single field manually
    const handleFieldChange = (field, value) => {
        setParsedLocation((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // Skip Location Action
    const handleSkip = async () => {
        const skippedLoc = {
            fullAddress: "",
            location: {
                type: "Point",
                coordinates: [defaultCenter.lng, defaultCenter.lat]
            },
            houseNumber: "",
            areaOrStreet: "",
            villageOrLocality: "",
            landmark: "",
            postalCode: "",
            city: "",
            state: "",
            country: "",
            rawData: "{}"
        };

        const mergedData = {
            ...registrationData,
            address: skippedLoc,
            temporaryLocation: "Location Skipped",
            permanentLocation: "Location Skipped",
        };

        toast.success('Location skipped. Please verify your email.');
        navigate('/verify-otp', {
            state: {
                registrationData: mergedData,
            },
        });
    };

    // Confirm
    const handleConfirm = async () => {
        if (!tempAddressText || tempAddressText.trim() === '') {
            toast.error('Please enter a valid address');
            return;
        }

        let finalLat = markerPos.lat;
        let finalLng = markerPos.lng;

        // If they never moved the map or searched, the pin is still exactly in Chennai.
        // We MUST geocode their manually typed text so we don't save incorrect coordinates.
        if (Math.abs(finalLat - 13.0827) < 0.0001 && Math.abs(finalLng - 80.2707) < 0.0001 && window.google) {
            toast.loading('Locating your typed address...', { id: 'save-geocode' });
            try {
                const geocoder = new window.google.maps.Geocoder();
                const results = await new Promise((resolve, reject) => {
                    geocoder.geocode({ address: tempAddressText }, (res, status) => {
                        if (status === 'OK' && res && res[0]) {
                            resolve(res);
                        } else {
                            reject(status);
                        }
                    });
                });
                const loc = results[0].geometry.location;
                finalLat = loc.lat();
                finalLng = loc.lng();
                toast.dismiss('save-geocode');
            } catch (err) {
                toast.dismiss('save-geocode');
                toast.error('Could not map this address. Please use the map search or drag the pin.');
                return; // Stop them from saving a bad location
            }
        }

        const addressObj = {
            fullAddress: tempAddressText,
            location: {
                type: "Point",
                coordinates: [finalLng, finalLat]
            },
            houseNumber: parsedLocation.houseNumber || "",
            areaOrStreet: parsedLocation.areaOrStreet || "",
            villageOrLocality: parsedLocation.villageOrLocality || "",
            landmark: parsedLocation.landmark || "",
            postalCode: parsedLocation.postalCode || "",
            city: parsedLocation.city || "",
            state: parsedLocation.state || "",
            country: parsedLocation.country || "",
            rawData: makeRawData(finalLat, finalLng, tempAddressText, parsedLocation)
        };

        if (
            !addressObj.houseNumber &&
            !addressObj.areaOrStreet &&
            !addressObj.villageOrLocality &&
            !addressObj.landmark &&
            !addressObj.postalCode &&
            !addressObj.city &&
            !addressObj.state &&
            !addressObj.country
        ) {
            toast.error('Could not extract location details. Please provide a more specific address.');
            return;
        }

        const mergedData = {
            ...registrationData,
            address: addressObj,
            temporaryLocation: tempAddressText,
            permanentLocation: permAddressText,
        };

        toast.success('Location updated. Please verify your email.');
        navigate('/verify-otp', {
            state: {
                registrationData: mergedData,
            },
        });
    };

    if (loadError) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0fdfa' }}>
                <p style={{ color: '#ef4444', fontSize: '15px', fontWeight: '600' }}>Failed to load Google Maps. Check your API key.</p>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh', width: '100%',
            background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 40%, #faf5ff 80%, #fff7ed 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px 16px',
            position: 'relative',
            animation: 'fadeIn 0.5s ease-out',
        }}>

            {/* Absolute styled Skip Button in the top right corner of the viewport */}
            <button
                onClick={handleSkip}
                disabled={loading}
                style={{
                    position: 'absolute',
                    top: '24px',
                    right: '24px',
                    padding: '10px 18px',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(226,232,240,0.8)',
                    background: 'rgba(255,255,255,0.92)',
                    color: '#475569',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    boxShadow: '0 4px 12px rgba(15,23,42,0.05)',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    backdropFilter: 'blur(8px)',
                    zIndex: 10,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#14b8a6';
                    e.currentTarget.style.color = '#0d9488';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(20,184,166,0.12)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(226,232,240,0.8)';
                    e.currentTarget.style.color = '#475569';
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(15,23,42,0.05)';
                }}
            >
                Skip Location Setup →
            </button>

            <div style={{ width: '100%', maxWidth: '680px' }}>

                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '22px' }}>
                    <div style={{
                        width: '52px', height: '52px', borderRadius: '15px',
                        background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 12px',
                        boxShadow: '0 8px 28px rgba(20,184,166,0.28)',
                    }}>
                        <MapPin size={26} color="white" />
                    </div>
                    <h1 style={{ color: '#0f172a', fontSize: '24px', fontWeight: '800', letterSpacing: '-0.5px' }}>
                        Set Your <span style={{ color: '#14b8a6' }}>Store Location</span>
                    </h1>
                    <p style={{ color: '#64748b', fontSize: '13px', marginTop: '5px', fontWeight: '500' }}>
                        Use the search panel or click on the map to pin your exact location
                    </p>
                </div>

                {/* Card */}
                <div style={{
                    background: 'rgba(255,255,255,0.97)',
                    borderRadius: '22px',
                    boxShadow: '0 20px 60px -12px rgba(15,23,42,0.14)',
                    border: '1px solid rgba(226,232,240,0.7)',
                    overflow: 'hidden',
                }}>
                    {/* Top accent */}
                    <div style={{ height: '4px', background: 'linear-gradient(90deg, #14b8a6, #06b6d4, #6366f1)' }} />

                    <div style={{ padding: '22px' }}>
                        {/* Search Row: Places input + side Map button */}
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                            Search shop or location
                        </label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <Search size={17} style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchValue}
                                    onChange={handleSearchInput}
                                    placeholder="Type a shop name, area, city, or coordinates..."
                                    disabled={!isLoaded}
                                    style={{
                                        width: '100%', padding: '13px 40px 13px 42px',
                                        borderRadius: '11px', border: '2px solid #e2e8f0',
                                        fontSize: '14px', fontWeight: '500', color: '#0f172a',
                                        outline: 'none', background: '#f8fafc',
                                        transition: 'all 0.2s',
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#14b8a6';
                                        e.target.style.background = '#fff';
                                        e.target.style.boxShadow = '0 0 0 4px rgba(20,184,166,0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e2e8f0';
                                        e.target.style.background = '#f8fafc';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                                {searchValue && (
                                    <button
                                        onClick={() => { setSearchValue(''); setSearchResults([]); setAddress(''); }}
                                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}
                                    >
                                        <X size={15} />
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsMapModalOpen(true)}
                                style={{
                                    padding: '12px 16px',
                                    borderRadius: '11px',
                                    border: '2px solid #14b8a6',
                                    background: 'rgba(20,184,166,0.04)',
                                    color: '#0d9488',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontWeight: '700',
                                    fontSize: '13px',
                                    height: '48px',
                                    transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(20,184,166,0.1)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(20,184,166,0.04)'; }}
                            >
                                <Map size={18} />
                                Pick on Map
                            </button>
                        </div>

                        {/* Search Predictions dropdown */}
                        <div style={{ position: 'relative' }}>
                            {searching && (
                                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
                                    <Loader size={15} className="spin" /> Searching...
                                </div>
                            )}

                            {!searching && searchResults.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100,
                                    borderRadius: '12px', border: '1px solid #e2e8f0',
                                    overflow: 'hidden', background: 'white',
                                    boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
                                    maxHeight: '260px', overflowY: 'auto',
                                }}>
                                    {searchResults.map((result, i) => (
                                        <button
                                            key={result.place_id}
                                            onClick={() => selectPrediction(result)}
                                            style={{
                                                width: '100%', padding: '13px 16px',
                                                border: 'none', background: 'white',
                                                cursor: 'pointer', textAlign: 'left',
                                                display: 'flex', alignItems: 'center', gap: '12px',
                                                transition: 'background 0.15s',
                                                borderBottom: i < searchResults.length - 1 ? '1px solid #f1f5f9' : 'none',
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f0fdfa'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                        >
                                            <div style={{
                                                width: '34px', height: '34px', borderRadius: '10px',
                                                background: 'rgba(20,184,166,0.1)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}>
                                                <MapPin size={16} style={{ color: '#14b8a6' }} />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {result.structured_formatting?.main_text || result.description}
                                                </p>
                                                <p style={{ fontSize: '12px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {result.structured_formatting?.secondary_text || ''}
                                                </p>
                                            </div>
                                            <ChevronRight size={14} style={{ color: '#cbd5e1', flexShrink: 0 }} />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {!searching && searchValue.length >= 2 && searchResults.length === 0 && searchValue !== address && (
                                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
                                    No places found. Try another search or click 'Pick on Map'.
                                </div>
                            )}
                        </div>

                        {/* GPS Button */}
                        <button
                            onClick={getCurrentLocation}
                            disabled={locating || !isLoaded}
                            style={{
                                width: '100%', marginTop: '8px', marginBottom: '16px',
                                padding: '12px 16px', borderRadius: '11px',
                                border: '1.5px dashed #14b8a6',
                                background: 'rgba(20,184,166,0.04)',
                                color: '#0d9488', fontSize: '13px', fontWeight: '700',
                                cursor: locating || !isLoaded ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => { if (!locating) e.currentTarget.style.background = 'rgba(20,184,166,0.1)'; }}
                            onMouseLeave={(e) => { if (!locating) e.currentTarget.style.background = 'rgba(20,184,166,0.04)'; }}
                        >
                            {locating ? <><Loader size={16} className="spin" /> Detecting location...</> : <><Navigation size={16} /> Use My Current Location</>}
                        </button>

                        {/* ─── Full Address Textarea (Fully Editable!) ─── */}
                        <div style={{
                            padding: '16px',
                            background: '#ffffff',
                            borderRadius: '14px',
                            border: '1px solid #cbd5e1',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            marginTop: '16px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', marginBottom: '2px' }}>
                                <MapPin size={15} style={{ color: '#14b8a6' }} />
                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Full Address
                                </span>
                            </div>

                            <textarea
                                rows={3}
                                value={address}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setAddress(val);
                                    setTempAddressText(val);
                                    if (sameAsTemporary) {
                                        setPermAddressText(val);
                                    }
                                }}
                                placeholder="Search above, choose on map, or type your exact shop/store address details here..."
                                style={{
                                    width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1',
                                    borderRadius: '8px', fontSize: '13px', outline: 'none',
                                    background: '#f8fafc', fontFamily: 'inherit', resize: 'vertical',
                                    lineHeight: '1.5', transition: 'all 0.2s',
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#14b8a6'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                            {markerPos.lat !== 13.0827 && (
                                <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>
                                    Coordinates: {markerPos.lat.toFixed(6)}°, {markerPos.lng.toFixed(6)}°
                                </p>
                            )}
                        </div>
                        {/* Section 1: Temporary Address */}
                        {/* <div style={{
                            padding: '16px',
                            background: '#ffffff',
                            borderRadius: '14px',
                            border: '1px solid #cbd5e1',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            marginTop: '16px',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', marginBottom: '2px' }}>
                                <Edit3 size={15} style={{ color: '#14b8a6' }} />
                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Temporary Address
                                </span>
                            </div>

                            <textarea
                                rows={3}
                                value={tempAddressText}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setTempAddressText(val);
                                    if (sameAsTemporary) {
                                        setPermAddressText(val);
                                    }
                                }}
                                placeholder="Enter your temporary/shop address here..."
                                style={{
                                    width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1',
                                    borderRadius: '8px', fontSize: '13px', outline: 'none',
                                    background: '#f8fafc', fontFamily: 'inherit', resize: 'vertical',
                                    lineHeight: '1.5', transition: 'all 0.2s',
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#14b8a6'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                        </div> */}

                        {/* Section 2: Permanent Address */}
                        {/* <div style={{
                            padding: '16px',
                            background: '#ffffff',
                            borderRadius: '14px',
                            border: '1px solid #cbd5e1',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            marginTop: '12px',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', marginBottom: '2px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Edit3 size={15} style={{ color: '#6366f1' }} />
                                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Permanent Address
                                    </span>
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', color: '#0d9488', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={sameAsTemporary}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setSameAsTemporary(checked);
                                            if (checked) {
                                                setPermAddressText(tempAddressText);
                                            }
                                        }}
                                        style={{ cursor: 'pointer', accentColor: '#14b8a6' }}
                                    />
                                    Same as Temporary
                                </label>
                            </div>

                            <textarea
                                rows={3}
                                disabled={sameAsTemporary}
                                value={sameAsTemporary ? tempAddressText : permAddressText}
                                onChange={(e) => setPermAddressText(e.target.value)}
                                placeholder="Enter your permanent address here..."
                                style={{
                                    width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1',
                                    borderRadius: '8px', fontSize: '13px', outline: 'none',
                                    background: sameAsTemporary ? '#e2e8f0' : '#f8fafc',
                                    fontFamily: 'inherit', resize: 'vertical',
                                    lineHeight: '1.5', transition: 'all 0.2s',
                                }}
                                onFocus={(e) => { if (!sameAsTemporary) e.target.style.borderColor = '#14b8a6'; }}
                                onBlur={(e) => { if (!sameAsTemporary) e.target.style.borderColor = '#cbd5e1'; }}
                            />
                        </div> */}
                        {/* ─── Address Component Form Fields ─── */}
                        <div style={{
                            marginTop: '16px', marginBottom: '16px',
                            background: '#ffffff', borderRadius: '14px',
                            display: 'flex', flexDirection: 'column', gap: '14px',
                            animation: 'fadeIn 0.3s ease-out',
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '5px' }}>House / Shop No.</label>
                                    <input
                                        type="text"
                                        value={parsedLocation.houseNumber}
                                        onChange={(e) => handleFieldChange('houseNumber', e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#f8fafc' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '5px' }}>Area / Street</label>
                                    <input
                                        type="text"
                                        value={parsedLocation.areaOrStreet}
                                        onChange={(e) => handleFieldChange('areaOrStreet', e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#f8fafc' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '5px' }}>Village / Locality</label>
                                    <input
                                        type="text"
                                        value={parsedLocation.villageOrLocality}
                                        onChange={(e) => handleFieldChange('villageOrLocality', e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#f8fafc' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '5px' }}>Landmark</label>
                                    <input
                                        type="text"
                                        value={parsedLocation.landmark}
                                        onChange={(e) => handleFieldChange('landmark', e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#f8fafc' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '5px' }}>City</label>
                                    <input
                                        type="text"
                                        value={parsedLocation.city}
                                        onChange={(e) => handleFieldChange('city', e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#f8fafc' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '5px' }}>State</label>
                                    <input
                                        type="text"
                                        value={parsedLocation.state}
                                        onChange={(e) => handleFieldChange('state', e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#f8fafc' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '5px' }}>Pincode</label>
                                    <input
                                        type="text"
                                        value={parsedLocation.postalCode}
                                        onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#f8fafc' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '5px' }}>Country</label>
                                    <input
                                        type="text"
                                        value={parsedLocation.country}
                                        onChange={(e) => handleFieldChange('country', e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#f8fafc' }}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* )} */}



                        {/* Confirm Button */}
                        <button
                            onClick={handleConfirm}
                            disabled={loading || !address || !isLoaded}
                            className="btn-primary"
                            style={{
                                width: '100%', marginTop: '16px',
                                justifyContent: 'center', height: '52px',
                                fontSize: '15px', fontWeight: '700', borderRadius: '12px',
                                opacity: (!address || !isLoaded) ? 0.45 : 1,
                                cursor: (!address || !isLoaded) ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {loading ? <div className="spinner" style={{ width: '20px', height: '20px' }} /> : <><CheckCircle size={20} /> Confirm Location & Continue</>}
                        </button>
                    </div>
                </div>

                {/* Back */}
                <button
                    onClick={() => {
                        Swal.fire({
                            title: 'Are you sure?',
                            text: 'If you go back, Entered location and registered data will be lost.',
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: '#14b8a6',
                            cancelButtonColor: '#ef4444',
                            confirmButtonText: 'Yes, go back',
                            cancelButtonText: 'Cancel'
                        }).then((result) => {
                            if (result.isConfirmed) {
                                navigate('/register');
                            }
                        });
                    }}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        margin: '18px auto 0', background: 'none', border: 'none',
                        color: '#64748b', cursor: 'pointer', fontSize: '14px',
                        fontWeight: '600', transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#0f172a'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                >
                    <ArrowLeft size={16} /> Back to Registration
                </button>
            </div>

            {/* Map Selection Modal Popup */}
            {isMapModalOpen && (
                <>
                <style>{`
                    .hide-scrollbar::-webkit-scrollbar {
                        display: none;
                    }
                    .hide-scrollbar {
                        -ms-overflow-style: none;
                        scrollbar-width: none;
                    }
                `}</style>
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.6)',
                    backdropFilter: 'blur(8px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 9999, padding: '20px',
                    animation: 'fadeIn 0.2s ease-out',
                }}>
                    <div className="hide-scrollbar" style={{
                        background: '#ffffff',
                        borderRadius: '24px',
                        width: '100%', maxWidth: '600px',
                        maxHeight: 'calc(100vh - 40px)',
                        overflowY: 'auto',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        border: '1px solid rgba(226, 232, 240, 0.8)',
                        display: 'flex', flexDirection: 'column',
                    }}>
                        {/* Header */}
                        <div style={{
                            padding: '18px 24px',
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: '#fafbfc'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: 'rgba(20,184,166,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <Map size={18} color="#0d9488" />
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                                        Select Shop Location
                                    </h3>
                                    <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0 0' }}>
                                        Drag the pin or click on the map to choose
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsMapModalOpen(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Content / Map */}
                        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

                            {/* Search inside modal */}
                            <div style={{ position: 'relative' }}>
                                <div style={{ position: 'relative' }}>
                                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                    <input
                                        type="text"
                                        value={modalSearchValue}
                                        onChange={handleModalSearchInput}
                                        placeholder="Search a place to jump to..."
                                        style={{
                                            width: '100%', padding: '11px 36px 11px 38px',
                                            borderRadius: '10px', border: '2px solid #e2e8f0',
                                            fontSize: '13px', fontWeight: '500', color: '#0f172a',
                                            outline: 'none', background: '#f8fafc',
                                            transition: 'all 0.2s',
                                        }}
                                        onFocus={(e) => { e.target.style.borderColor = '#14b8a6'; e.target.style.background = '#fff'; }}
                                        onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                                    />
                                    {modalSearchValue && (
                                        <button
                                            onClick={() => { setModalSearchValue(''); setModalSearchResults([]); }}
                                            style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>

                                {/* Modal Search Results Dropdown */}
                                {modalSearching && (
                                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, marginTop: '4px', background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', padding: '12px', textAlign: 'center', color: '#64748b', fontSize: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
                                        <Loader size={14} className="spin" style={{ marginRight: '6px' }} /> Searching...
                                    </div>
                                )}
                                {!modalSearching && modalSearchResults.length > 0 && (
                                    <div style={{
                                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                                        marginTop: '4px', borderRadius: '10px', border: '1px solid #e2e8f0',
                                        overflow: 'hidden', background: 'white',
                                        boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
                                        maxHeight: '200px', overflowY: 'auto',
                                    }}>
                                        {modalSearchResults.map((result, i) => (
                                            <button
                                                key={result.place_id}
                                                onClick={() => selectModalPrediction(result)}
                                                style={{
                                                    width: '100%', padding: '10px 14px',
                                                    border: 'none', background: 'white',
                                                    cursor: 'pointer', textAlign: 'left',
                                                    display: 'flex', alignItems: 'center', gap: '10px',
                                                    transition: 'background 0.15s',
                                                    borderBottom: i < modalSearchResults.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                    fontSize: '12px',
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = '#f0fdfa'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                                            >
                                                <MapPin size={14} style={{ color: '#14b8a6', flexShrink: 0 }} />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <p style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {result.structured_formatting?.main_text || result.description}
                                                    </p>
                                                    <p style={{ fontSize: '11px', color: '#64748b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {result.structured_formatting?.secondary_text || ''}
                                                    </p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Map container */}
                            <div style={{
                                borderRadius: '16px', overflow: 'hidden',
                                border: '2px solid #e2e8f0', height: '380px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.07)',
                                position: 'relative'
                            }}>
                                {!isLoaded ? (
                                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', flexDirection: 'column', gap: '10px' }}>
                                        <Loader size={28} style={{ color: '#14b8a6' }} className="spin" />
                                        <p style={{ color: '#64748b', fontSize: '13px' }}>Loading Google Maps...</p>
                                    </div>
                                ) : (
                                    <GoogleMap
                                        mapContainerStyle={{ width: '100%', height: '100%' }}
                                        center={markerPos}
                                        zoom={16}
                                        onClick={(e) => {
                                            const lat = e.latLng.lat();
                                            const lng = e.latLng.lng();
                                            lastGeocodedPosRef.current = null;
                                            setMarkerPos({ lat, lng });
                                            reverseGeocode(lat, lng);
                                        }}
                                        onLoad={(map) => {
                                            mapInstanceRef.current = map;
                                            setMapInstance(map);
                                        }}
                                        onIdle={() => {
                                            if (mapIdleDebounceRef.current) clearTimeout(mapIdleDebounceRef.current);
                                            mapIdleDebounceRef.current = setTimeout(() => {
                                                const map = mapInstanceRef.current;
                                                if (map) {
                                                    const center = map.getCenter();
                                                    if (center) {
                                                        const lat = parseFloat(center.lat().toFixed(6));
                                                        const lng = parseFloat(center.lng().toFixed(6));
                                                        const last = lastGeocodedPosRef.current;
                                                        if (last && Math.abs(last.lat - lat) < 0.00005 && Math.abs(last.lng - lng) < 0.00005) return;
                                                        lastGeocodedPosRef.current = { lat, lng };
                                                        setMarkerPos({ lat, lng });
                                                        reverseGeocode(lat, lng);
                                                    }
                                                }
                                            }, 600);
                                        }}
                                        options={{
                                            zoomControl: true,
                                            mapTypeControl: true,
                                            streetViewControl: false,
                                            fullscreenControl: false,
                                            clickableIcons: false,
                                            mapTypeControlOptions: {
                                                position: window.google?.maps?.ControlPosition?.TOP_RIGHT,
                                                style: window.google?.maps?.MapTypeControlStyle?.HORIZONTAL_BAR,
                                            },
                                        }}
                                    >
                                        {/* Center pin overlay is used instead of Marker */}
                                    </GoogleMap>
                                )}

                                {/* Fixed center pin overlay */}
                                {isLoaded && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%', left: '50%',
                                        transform: 'translate(-50%, -100%)',
                                        pointerEvents: 'none',
                                        zIndex: 10,
                                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    }}>
                                        <div style={{
                                            width: '40px', height: '40px',
                                            background: '#ef4444',
                                            borderRadius: '50% 50% 50% 0',
                                            transform: 'rotate(-45deg)',
                                            border: '3px solid #fff',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <div style={{
                                                width: '12px', height: '12px',
                                                background: '#fff',
                                                borderRadius: '50%',
                                                transform: 'rotate(45deg)',
                                            }} />
                                        </div>
                                    </div>
                                )}

                                {/* Move instruction overlay */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: '12px', left: '50%',
                                    transform: 'translateX(-50%)',
                                    background: 'rgba(15, 23, 42, 0.8)',
                                    backdropFilter: 'blur(4px)',
                                    color: '#fff',
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    pointerEvents: 'none',
                                    whiteSpace: 'nowrap',
                                    zIndex: 10,
                                }}>
                                    📍 Drag the map to position the pin on your shop
                                </div>
                            </div>

                            {/* GPS inside modal */}
                            <button
                                type="button"
                                onClick={getCurrentLocation}
                                disabled={locating || !isLoaded}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px', borderRadius: '10px',
                                    border: '1.5px dashed #14b8a6',
                                    background: 'rgba(20,184,166,0.02)',
                                    color: '#0d9488', fontSize: '12px', fontWeight: '700',
                                    cursor: locating || !isLoaded ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                }}
                            >
                                {locating ? <><Loader size={14} className="spin" /> Detecting...</> : <><Navigation size={14} /> Center on My Current Location</>}
                            </button>

                            {/* Selected address display */}
                            <div style={{
                                padding: '12px 14px',
                                background: address ? 'linear-gradient(135deg, #f0fdfa, #ecfeff)' : '#f8fafc',
                                borderRadius: '12px',
                                border: address ? '1.5px solid rgba(20,184,166,0.25)' : '1px solid #e2e8f0',
                                display: 'flex', gap: '8px', alignItems: 'flex-start',
                                transition: 'all 0.3s ease',
                            }}>
                                <MapPin size={16} color={address ? '#14b8a6' : '#94a3b8'} style={{ marginTop: '2px', flexShrink: 0 }} />
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: '11px', fontWeight: '700', color: '#0d9488', textTransform: 'uppercase', margin: '0 0 3px 0' }}>
                                        Selected Address
                                    </p>
                                    <p style={{ fontSize: '12.5px', color: '#334155', lineHeight: '1.4', margin: 0 }}>
                                        {address || "No address selected yet. Click or drag the map."}
                                    </p>
                                    {address && (
                                        <p style={{ fontSize: '10px', color: '#94a3b8', margin: '4px 0 0 0' }}>
                                            📍 {markerPos.lat.toFixed(6)}°, {markerPos.lng.toFixed(6)}°
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '16px 24px',
                            borderTop: '1px solid #f1f5f9',
                            display: 'flex', justifyContent: 'flex-end', gap: '12px',
                            background: '#fafbfc'
                        }}>
                            <button
                                onClick={() => setIsMapModalOpen(false)}
                                style={{
                                    padding: '10px 18px', borderRadius: '10px',
                                    border: '1px solid #cbd5e1', background: '#fff',
                                    color: '#475569', fontSize: '13px', fontWeight: '700',
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setIsMapModalOpen(false);
                                    toast.success("Location chosen successfully!");
                                }}
                                disabled={!address}
                                style={{
                                    padding: '10px 22px', borderRadius: '10px',
                                    border: 'none', background: address ? '#14b8a6' : '#cbd5e1',
                                    color: '#fff', fontSize: '13px', fontWeight: '700',
                                    cursor: address ? 'pointer' : 'not-allowed', transition: 'all 0.2s',
                                    boxShadow: address ? '0 4px 12px rgba(20,184,166,0.25)' : 'none',
                                }}
                            >
                                Done / Confirm
                            </button>
                        </div>
                    </div>
                </div>
                </>
            )}

            <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .pac-container {
          border-radius: 12px !important;
          border: 1px solid #e2e8f0 !important;
          box-shadow: 0 12px 32px rgba(0,0,0,0.1) !important;
          margin-top: 6px !important;
          font-family: 'Poppins', sans-serif !important;
        }
        .pac-item { padding: 10px 14px !important; font-size: 13px !important; cursor: pointer !important; }
        .pac-item:hover { background: #f0fdfa !important; }
        .pac-item-query { color: #0f172a !important; font-weight: 600 !important; }
      `}</style>
        </div>
    );
}
